import React, { useEffect, useState } from 'react'
import { Player } from '@lottiefiles/react-lottie-player'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'

import { CurrentSubView, PermissionStatus, RecognitionState } from '../../../models/enums'
import animation from '../../../assets/lottie/recording.json'

import Modal from '../../Common/Modal'
import * as Icons from '../../Icons/MenuIcons'
import MicrophoneLoader from '../../Loader/MicrophoneLoader'
import MicrophoneModalWaiting from './MicrophoneModal/MicrophoneModalWaiting'
import MicrophoneModalRefused from './MicrophoneModal/MicrophoneModalRefused'

interface MicrophoneButtonProps {
  openMicrophone?: boolean
}

const MicrophoneButton = ({ openMicrophone }: MicrophoneButtonProps): JSX.Element => {
  const dispatch = useDispatch()
  const isMicrophoneAllowed = useSelector((state: RootState) => state.speechReducer.isMicrophoneAllowed)
  const showMicrophoneModal = useSelector((state: RootState) => state.speechReducer.showMicrophoneModal)
  const microphonePermissionStatus = useSelector((state: RootState) => state.speechReducer.permissionStatus)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const themeColors = useSelector((state: RootState) => state.viewReducer.themeColors)
  const [playerRef, setPlayerRef] = useState<any>(null)

  const handleClick = (): void => {
    if (!isMicrophoneAllowed) {
      dispatch(storeActions.speech.askPermission())
    } else {
      dispatch(storeActions.speech.toggleMicrophone(false))
    }
  }

  useEffect(() => {
    console.log('openMicrophone : ', openMicrophone)
    openMicrophone && handleClick()
  }, [])

  useEffect(() => {
    dispatch(storeActions.speech.setIsMicrophoneAllowed(microphonePermissionStatus === PermissionStatus.allowed))
    switch (microphonePermissionStatus) {
      case PermissionStatus.allowed:
        dispatch(storeActions.speech.setShowMicrophoneModal(false))
        dispatch(storeActions.speech.toggleMicrophone(false))
        break
      case PermissionStatus.waiting:
      case PermissionStatus.refused:
      case PermissionStatus.error:
        break
    }
  }, [microphonePermissionStatus])

  useEffect(() => {
    if (playerRef) {
      activeRecognitionState === RecognitionState.Listening ? playerRef.lottie.play() : playerRef.lottie.stop()
    }

    dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.none))
    dispatch(storeActions.utils.setFullScreenImage(undefined))
  }, [activeRecognitionState, playerRef])

  return (
    <React.Fragment>
      <button
        className={`rf-relative large:rf-w-full large:rf-h-auto rf-aspect-square rf-flex rf-justify-center rf-items-center rf-border ${
          activeRecognitionState === RecognitionState.Listening ? (isMobile ? 'rf-h-12 rf-border-[#00000029]' : 'rf-border-[#00000029]') : isMobile ? 'rf-h-8 rf-border-none' : 'rf-border-menuBorder'
        } rf-rounded-half rf-overflow-hidden`}
        onClick={handleClick}
        aria-label='microphone'
        style={{
          boxShadow: activeRecognitionState === RecognitionState.Listening ? 'inset 0 0 6px 3px#00000029' : isMobile ? undefined : '0 0 20px 1px #A4A7AF80',
          WebkitMaskImage: activeRecognitionState === RecognitionState.Listening ? '-webkit-radial-gradient(white, black)' : undefined
        }}
      >
        <div className='rf-absolute rf-h-full rf-w-full rf-flex rf-justify-end'>
          {activeRecognitionState === RecognitionState.Listening && (
            <Player
              lottieRef={(instance): void => {
                setPlayerRef({ lottie: instance })
              }}
              loop
              src={animation}
              className='rf-w-full'
            />
          )}
        </div>

        {/* Display microphone icon or loader depending on speech recognition state */}
        {activeRecognitionState === RecognitionState.Listening ? (
          <Icons.MicrophoneIconOn className='rf-h-3 rf-w-3 large:rf-h-4 large:rf-w-4' />
        ) : activeRecognitionState === RecognitionState.Closed ? (
          <Icons.MicrophoneIcon className='rf-h-5 rf-w-5' color={themeColors.secondary} />
        ) : (
          <MicrophoneLoader />
        )}
      </button>

      <Modal isOpened={showMicrophoneModal} onClose={() => dispatch(storeActions.speech.setShowMicrophoneModal(false))}>
        {microphonePermissionStatus === PermissionStatus.waiting && <MicrophoneModalWaiting />}
        {microphonePermissionStatus === PermissionStatus.refused && <MicrophoneModalRefused />}
      </Modal>
    </React.Fragment>
  )
}

export default MicrophoneButton
